import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import { Class as ClassityClass } from '@classity-edtech/ui-questionnaire';
import { style, useLocalStorage, windowExists } from '@classity-edtech/ui-base';
import { CircularProgress } from '@mui/material';
import { FreeFormName, TutorType } from '@classity-edtech/platform-utils';
import { webPaths } from '../../routes/paths';
import Helmet from '../../components/Helmet';
import { useGetQuestionnaireQuery } from '../../services/questionnaire';
import { useCreateFreeFormEventLogMutation } from '../../services/eventLogs';
import { useGetMyProfileQuery } from '../../services/users';

export interface HomeProps extends MuiBoxProps {}

export const Questionnaire = ({ className }: HomeProps) => {
  const navigate = useNavigate();
  const formName = FreeFormName.CLASS_QUESTIONNAIRE;

  const webUi = (windowExists.__UI_CONFIG__ as any)?.webUi?.url;

  const handleHomeNavigation = () => {
    (windowExists as any).location.href = `${webUi}/`;
  };

  const handleDashboardNavigation = () => {
    navigate(`/${webPaths.tutorDashboard}`);
  };

  const {
    data: user,
    isLoading: loadingUser,
    isUninitialized: uninitialisedUser,
  } = useGetMyProfileQuery();

  const [searchParams, setSearchParams] = useSearchParams();
  const tutorType = searchParams.get('tutorType') as TutorType;

  const [lastQuestionnaire, setLastQuestionnaire] = useLocalStorage('lastQuestionnaire', '');
  const { data: form, isLoading: loadingForm } = useGetQuestionnaireQuery({
    name: FreeFormName.CLASS_QUESTIONNAIRE,
  });

  const [createEvent] = useCreateFreeFormEventLogMutation();

  const handleNavigation = async (screen: string) => {
    if (screen.includes('/')) return navigate(screen);
    setSearchParams({ screen, formName });
    setLastQuestionnaire({ screen, formName });
  };

  const handleSubmissionEmail = async ({
    firstName,
    email,
  }: {
    firstName: string;
    email: string;
  }) => {};

  useEffect(() => {
    if (!!lastQuestionnaire.screen) {
      setSearchParams({
        screen: lastQuestionnaire.screen,
        formName: lastQuestionnaire.formName,
      });
    } else {
      setSearchParams({
        screen: 'promotion',
        formName,
      });
    }
  }, [form]);

  useEffect(() => {
    if (!user && !loadingUser && !uninitialisedUser) {
      localStorage.removeItem('token');
      navigate(`${webPaths.root}`, { replace: true });
    }
  }, [user]);

  return (
    <>
      <Helmet />
      {loadingForm ? (
        <Box className={`${className} questionnaire__loader`}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {form?.controls && (
            <ClassityClass
              tutorType={tutorType || TutorType.CLASSITY}
              navigateToHomePage={handleHomeNavigation}
              navigateToDashboardPage={handleDashboardNavigation}
              screen={searchParams.get('screen')?.toString() || 'topic'}
              navigate={handleNavigation}
              submissionEmail={handleSubmissionEmail}
              formName={FreeFormName.CLASS_QUESTIONNAIRE}
              formControls={form.controls}
            />
          )}
        </>
      )}
    </>
  );
};

Questionnaire.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Questionnaire.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Questionnaire)(
  ({ theme }) => `
  &.MuiBox-root {
    background-color: ${theme.palette.background.paper};
  }

  &.questionnaire__loader {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    background-color: ${style.colors.darkGray[50]}
  }
`,
);
